import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Search as SearchIcon, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { projectListColumns } from "@patronix/domain/list";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import { ImportPanel } from "@/features/import/ImportPanel";
import {
  isProjectFilter,
  listProjects,
  projectDetailQuery,
  projectFilters,
  projectKeys,
  projectListQuery,
  projectStatuses,
  projectTone,
  type ProjectFilter,
  type ProjectRow,
} from "@/features/project/api";
import { ProjectPanel } from "@/features/project/ProjectPanel";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { listView, noView, viewQuery, viewSearch, type ViewSearch } from "@/lib/listView";
import { sessionContextQuery, useCan } from "@/lib/session";

// Project → Projects of the active company. Keyset on code through
// project_page (projects_list_idx); detail = one document RPC with the work
// breakdown and latest time, prefetched on hover. Structures and time are
// imported (ADR-034): projects (one row per activity) and project_time.

interface Search extends ViewSearch {
  status: ProjectFilter;
  q: string;
  id?: string | undefined;
  import?: true | undefined;
}

const invalidateAfterImport = [projectKeys.all] as const;

export const Route = createFileRoute("/_app/project/projects")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      status: isProjectFilter(raw.status) ? raw.status : "open",
      q: typeof raw.q === "string" ? raw.q.slice(0, 100) : "",
    };
    if (typeof raw.id === "string" && raw.id) s.id = raw.id;
    if (raw.import === true || raw.import === "true") s.import = true;
    return { ...s, ...viewSearch(projectListColumns, raw) };
  },
  loaderDeps: ({ search }) => ({ status: search.status, q: search.q, f: search.f, s: search.s }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(projectListQuery({ companyId, status: deps.status, q: deps.q, ...viewQuery(projectListColumns, deps) }));
  },
  component: ProjectsPage,
});

function ProjectsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCan("project.plan.manage");
  const canBook = useCan("project.time.book");
  const canImport = useCan("core.data.import") && (canManage || canBook);
  const { company } = useActiveCompany();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params = { companyId: company?.id ?? "", status: search.status, q: search.q, ...viewQuery(projectListColumns, search) };
  const query = useInfiniteQuery(projectListQuery(params));
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, id: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const columns: Column<ProjectRow>[] = [
    {
      key: "code",
      header: t("project.colCode"),
      width: "minmax(110px,0.8fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.code}</span>,
    },
    { key: "name", header: t("project.colName"), width: "minmax(150px,1.6fr)", cell: (r) => <span className="truncate">{r.name}</span> },
    {
      key: "customer",
      header: t("project.customer"),
      width: "minmax(100px,1fr)",
      className: "text-ink-soft",
      cell: (r) => <span className="truncate">{r.customer_name ?? "—"}</span>,
    },
    {
      key: "start",
      header: t("project.startDate"),
      width: "minmax(92px,0.7fr)",
      className: "text-ink-soft",
      cell: (r) => <span className="tabular">{r.start_date ? fmt.dayYear(r.start_date) : "—"}</span>,
    },
    {
      key: "end",
      header: t("project.endDate"),
      width: "minmax(92px,0.7fr)",
      className: "text-ink-soft",
      cell: (r) => <span className="tabular">{r.end_date ? fmt.dayYear(r.end_date) : "—"}</span>,
    },
    {
      key: "hours",
      header: t("project.colHoursBookedPlanned"),
      width: "minmax(80px,0.7fr)",
      align: "end",
      cell: (r) => (
        <span className="tabular">
          {fmt.qty(String(r.booked_hours))} / {fmt.qty(String(r.planned_hours))}
        </span>
      ),
    },
    {
      key: "status",
      header: t("po.colStatus"),
      width: "minmax(96px,0.6fr)",
      cell: (r) => <Badge tone={projectTone[r.status]}>{t(`project.status.${r.status}`)}</Badge>,
      options: projectStatuses.map((v) => ({ value: v, label: t(`project.status.${v}`) })),
    },
  ];
  const exportColumns: ExportColumn<ProjectRow>[] = [
    { header: "project_code", kind: "code", value: (r) => r.code },
    { header: "project_name", kind: "text", value: (r) => r.name },
    { header: "customer", kind: "code", value: (r) => r.customer_code },
    { header: "customer_name", kind: "text", value: (r) => r.customer_name },
    { header: "manager", kind: "text", value: (r) => r.manager_name },
    { header: "start_date", kind: "date", value: (r) => r.start_date },
    { header: "end_date", kind: "date", value: (r) => r.end_date },
    { header: "activities", kind: "number", value: (r) => r.activity_count },
    { header: "planned_hours", kind: "number", value: (r) => Number(r.planned_hours) },
    { header: "booked_hours", kind: "number", value: (r) => Number(r.booked_hours) },
    { header: "status", kind: "code", value: (r) => r.status },
  ];
  const label = (f: ProjectFilter) => (f === "all" ? t("po.filterAll") : t(`project.filter.${f}`));

  const listTools: ListTools<ProjectRow> = {
    id: "projects",
    title: t("project.title"),
    onNew: canManage ? () => setSearch({ id: "new", import: undefined }) : undefined,
    export: { fileBase: "projects", sheet: t("project.title"), columns: exportColumns, fetchPage: (cursor, signal) => listProjects(params, cursor, signal, EXPORT_PAGE) },
    onImport: canImport ? () => setSearch({ import: true, id: undefined }) : undefined,
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.project.title")} <span aria-hidden="true">/</span> {t("nav.project.projects")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("project.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setSearch({ id: "new", import: undefined })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("project.new")}
          </button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("po.colStatus")} className="flex flex-wrap gap-2">
          {projectFilters.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={search.status === f}
              onClick={() => setSearch({ status: f, id: undefined })}
              className={cn(
                "h-8 rounded-full border px-3 text-[13px] transition-colors",
                search.status === f ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
              )}
            >
              {label(f)}
            </button>
          ))}
        </div>
        <div className="grow" />
        <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
          <SearchIcon size={15} aria-hidden="true" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("project.searchPlaceholder")}
            aria-label={t("project.searchPlaceholder")}
            data-list-search
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {text && (
            <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
              <X size={14} />
            </button>
          )}
        </label>
      </div>

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("project.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.id}
          onSelect={(r) => setSearch({ id: r.id, import: undefined })}
          onClear={() => setSearch({ id: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(projectDetailQuery(r.id))}
          emptyTitle={t("project.emptyTitle")}
          emptyBody={t("project.emptyBody")}
          emptyAction={
            canManage ? (
              <button
                type="button"
                onClick={() => setSearch({ id: "new", import: undefined })}
                className="h-9 rounded-xl bg-accent px-3.5 text-[13px] font-medium text-on-accent hover:bg-accent-hover"
              >
                {t("project.new")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setText("");
                  setSearch({ status: "all", q: "", id: undefined, ...noView });
                }}
                className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
              >
                {t("list.clearFilters")}
              </button>
            )
          }
          minWidth={740}
          tools={listTools}
          view={listView(projectListColumns, search, setSearch)}
        />
        <DetailPanel
          label={search.import ? t("import.title") : t("project.detailLabel")}
          open={!!search.id || !!search.import}
          placeholder={<DetailPlaceholder title={t("project.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {search.import ? (
            <ImportPanel
              entities={canManage ? ["projects", "project_time"] : ["project_time"]}
              initial={canManage ? "projects" : "project_time"}
              onClose={() => setSearch({ import: undefined })}
              invalidate={invalidateAfterImport}
            />
          ) : (
            search.id && <ProjectPanel key={search.id} id={search.id} onClose={() => setSearch({ id: undefined })} onOpen={(id) => setSearch({ id })} />
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
